import clsx from "clsx";
import objectHash from "object-hash";
import React, {
  useLayoutEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import PhoneInput2, { CountryData } from "react-phone-input-2";
import "./PhoneInput.css";
import { PhoneInputPropsModel, PhoneInputRefModel } from "./tools/PhoneInput.model";
import {
  getCountryCodes,
  getCountryLocalizations,
} from "./tools/PhoneInput.utils";
import { useCustomFormWcagAttributes } from "common-react/src/hooks/useCustomFormWcagAttributes";
import { FieldLabel } from "../FieldLabel/FieldLabel";
import { useFocusVisibleRef } from "common-react";

import useStyles from "./PhoneInput.style";

export const PhoneInput = forwardRef<PhoneInputRefModel, PhoneInputPropsModel>(
  (
    {
      placeholder,
      countries,
      disabled = false,
      required = true,
      uniqueId,
      ariaRequiredId,
      hasError,
      country = "pl",
      searchPlaceholder,
      onChange,
      onBlur,
      value,
      name,
      label,
      className,
      disableDropdown,
      fullWidth = false,
    },
    refFromParent
  ) => {
    const phoneInputElem = useRef<PhoneInputRefModel>(null);
    const [innerCountryCode, setInnerCountryCode] = useState<string | null>(null);
    const c = useStyles({});
    const { isFocusVisible, ref: focusVisibleRef } = useFocusVisibleRef();
    const [isFocusedInput, setIsFocusedInput] = useState(false);
    const {
      uniqueIdForAriaRequired: innerUniqueIdForAriaRequired,
      uniqueId: innerUniqueId,
    } = useCustomFormWcagAttributes({
      fieldName: name,
    });

    useLayoutEffect(() => {
      // @ts-expect-error
      const numberInputRef = phoneInputElem?.current?.numberInputRef;
      if (numberInputRef) {
        focusVisibleRef(numberInputRef);
      }
    }, [focusVisibleRef]);

    useImperativeHandle(refFromParent, () => phoneInputElem.current as PhoneInputRefModel, []);

    const countryIsoCode = useMemo(() => {
      if (innerCountryCode) {
        return innerCountryCode.toUpperCase();
      }
      return (country as string)?.toUpperCase();
    }, [innerCountryCode, country]);

    return (
      <div className={clsx(c.root, className)}>
        <FieldLabel htmlFor={uniqueId || innerUniqueId}>{label}</FieldLabel>
        <PhoneInput2
          // @ts-ignore
          ref={phoneInputElem}
          key={objectHash(countries, { algorithm: 'md5', encoding: 'hex', ignoreUnknown: true })}
          disabled={disabled}
          disableDropdown={disableDropdown}
          country={country}
          onlyCountries={getCountryCodes(countries)}
          countryCodeEditable={false}
          enableSearchField={false}
          searchPlaceholder={searchPlaceholder}
          localization={getCountryLocalizations(countries)}
          placeholder={placeholder}
          value={value}
          onChange={(value, data, event, formattedValue) => {
            onChange?.(value, data, event, formattedValue);
            setInnerCountryCode((data as CountryData)?.countryCode ?? null);
          }}
          onFocus={() => setIsFocusedInput(true)}
          onBlur={(event, data) => {
            onBlur?.(event, data);
            setIsFocusedInput(false)
          }}
          inputClass={clsx(c.inputWrapper, hasError && c.inputError, isFocusVisible && "Mui-focusVisible")}
          buttonClass={c.dropdownButton}
          containerClass={clsx({
            [c.containerWrapper]: true,
            [c.isFocusedInput]: isFocusedInput,
            [c.isDisabled]: disabled,
            [c.isError]: hasError,
            [c.containerWrapperFullWidth]: fullWidth,
            "Mui-focusVisible": isFocusVisible
          })}
          dropdownClass={c.countryListWrapper}
          specialLabel={countryIsoCode}
          inputProps={{
            autoComplete: "off",
            id: uniqueId || innerUniqueId,
            "aria-required": required,
            "aria-describedby": ariaRequiredId || innerUniqueIdForAriaRequired,
            name,
          }}
          inputExtraProps={{
            name,
          }}
        />
      </div>
    );
  }
);

PhoneInput.displayName = "PhoneInput";
